<template>
    <div
        v-if="currentRegion"
        class="offices-controls"
    >
        <div class="wrapper">
            <div class="oc-wrapper flex items-center justify-between">
                <div class="oc-location flex items-center">
                    <div
                        v-click-outside="() => isOpenLocalList = false"
                        class="relative pr-40"
                    >
                        <UIButton
                            :label="currentRegion.title"
                            flat
                            :class="['oc-location__local', {open: isOpenLocalList}]"
                            @click="isOpenLocalList = !isOpenLocalList"
                        >
                            <template #icon>
                                <CheveronSvg />
                            </template>
                        </UIButton>
                        <transition
                            name="fade"
                            mode="out-in"
                        >
                            <FilterPopup
                                v-if="isOpenLocalList"
                                class="oc-location-modal-local"
                                @close="isOpenLocalList = false"
                            >
                                <div class="flex flex-column items-start">
                                    <template v-for="region in filtredRegions">
                                        <UIButton
                                            v-if="region"
                                            :key="region.id"
                                            flat
                                            :class="[
                                                'oc-location-region__item',
                                                {'current': currentRegion.title === region.title},
                                            ]"
                                            @click="onRegionChooseHandler(region)"
                                        >
                                            {{ region.title }}
                                        </UIButton>
                                    </template>
                                </div>
                            </FilterPopup>
                        </transition>
                    </div>
                    <div
                        v-click-outside="() => isOpenAllCities = false"
                        class="relative"
                    >
                        <UIButton
                            v-show="!hideCities"
                            flat
                            :class="['oc-location__all', {open: isOpenAllCities}]"
                            @click="isOpenAllCities = !isOpenAllCities"
                        >
                            <template #default>
                                <span v-html="currentCityName" />
                            </template>
                            <template #icon>
                                <CheveronSvg />
                            </template>
                        </UIButton>
                        <transition
                            name="fade"
                            mode="out-in"
                        >
                            <FilterPopup
                                v-if="isOpenAllCities"
                                class="oc-location-modal-local"
                                @close="isOpenAllCities = false"
                            >
                                <div
                                    v-for="(cityChunk, index) in splitToChunks(
                                        getFiltredAndSortedArray(currentRegion.cities, `${type}_count`), 15
                                    )"
                                    :key="index"
                                    class="oc-location-modal-local-wrapper flex flex-column items-start"
                                >
                                    <UIButton
                                        v-if="cityChunk.length > 1 && index === 0"
                                        flat
                                        :class="[
                                            'oc-location-region__item',
                                            {'current': !currentCity},
                                        ]"
                                        @click="onCityChooseHandler()"
                                    >
                                        <p class="relative">Все города</p>
                                    </UIButton>
                                    <UIButton
                                        v-for="city in cityChunk"
                                        :key="city.id"
                                        flat
                                        :class="[
                                            'oc-location-region__item',
                                            {'current': isCurrentCity(city)},
                                        ]"
                                        @click="onCityChooseHandler(city)"
                                    >
                                        <p
                                            class="relative"
                                            v-html="getFormattedCityTitle(city, false)"
                                        />
                                    </UIButton>
                                </div>
                            </FilterPopup>
                        </transition>
                    </div>
                </div>
                <div class="oc-features-wrapper flex items-center">
                    <div class="oc-features inline-flex items-center">
                        <div
                            v-for="({value, label}, index) in services.visibleServices"
                            :key="`${label}__${index}`"
                            class="checkbox"
                        >
                            <input
                                :id="`${value}_checkbox`"
                                v-model="activeVisibleServices"
                                type="checkbox"
                                :name="value"
                                :value="value"
                                class="checkbox-input"
                                hidden
                                @change="onChangeCheckboxHandler"
                            >
                            <label
                                :class="[
                                    'checkbox-label pointer relative',
                                    {checked: activeVisibleServices.includes(value)},
                                ]"
                                :for="`${value}_checkbox`"
                            >
                                {{ label }}
                            </label>
                        </div>
                    </div>
                    <div
                        v-if="isMoreServicesExists"
                        v-click-outside="() => isOpenServices = false"
                        class="oc-services"
                    >
                        <UIButton
                            flat
                            icon-position="left"
                            :class="['oc-services__btn relative', {open: isOpenServices}]"
                            @click="isOpenServices = true"
                        >
                            Ещё услуги
                            <span
                                v-show="hiddenActiveServicesCount"
                                class="oc-services__btn__count"
                            >
                                {{ hiddenActiveServicesCount }}
                            </span>
                            <template #icon>
                                <PointsSvg />
                            </template>
                        </UIButton>
                        <transition
                            name="fade"
                            mode="out-in"
                        >
                            <Modal
                                v-if="isOpenServices"
                                class="oc-services-modal"
                                @close="isOpenServices = false"
                            >
                                <ModalFilters
                                    v-model="activeVisibleServices"
                                    title="Услуги"
                                    :filters="services.moreServices"
                                    :active-filters="activeServices"
                                    @apply="setServices"
                                />
                            </Modal>
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {defineComponent, ref, watch, computed} from 'vue';
import clone from 'lodash.clonedeep';
import {UIButton} from '@cibsite/ui-components-vue2';

import {splitToChunks} from '@/core/utils';
import {servicesMap} from '@/core/models/pages/aboutPage/OfficesModel';

const MAIN_CITY = 'Ростов-на-Дону';

export default defineComponent({
    name: 'OfficesControls',
    components: {
        CheveronSvg: () => import('@/assets/images/icons/arrows/chevron-down-bold.svg?inline'),
        PointsSvg: () => import('@/assets/images/components/cards/points.svg?inline'),
        Modal: () => import('@/components/UI-components/Modal/UIModal.vue'),
        FilterPopup: () => import('@/components/UI-components/Popups/FilterPopup.vue'),
        ModalFilters: () => import('@/components/UI-components/Modal/ModalFiltres.vue'),
        UIButton,
    },
    props: {
        regions: {
            type: Array,
            required: true,
        },
        currentCity: {
            type: [Object],
            default: () => null,
        },
        currentRegion: {
            type: Object,
            required: true,
        },
        activeServices: {
            type: Array,
            default: () => [],
        },
        type: {
            type: String,
            default: () => 'office',
            validator: (value) => {
                return ['office', 'atm', 'terminal'].includes(value);
            },
        },
        hideCities: {
            type: Boolean,
            default: () => false,
        },
    },
    emits: [
        'update:region',
        'update:city',
        'update:services',
    ],
    setup(props, context) {
        const isOpenLocalList = ref(false);
        const isOpenAllCities = ref(false);
        const isOpenServices = ref(false);
        const activeVisibleServices = ref([]);
        const services = ref({
            visibleServices: [],
            moreServices: [],
        });

        const filtredRegions = computed(() => {
            return props.regions.reduce((acc, region) => {
                if (region?.cities.filter((city) => city[`${props.type}_count`])?.length) {
                    acc.push(region);
                }

                return acc;
            }, []);
        });

        const filters = computed(() => {
            return Object.keys(servicesMap[props.type]).map((key) => {
                return {
                    value: key,
                    label: servicesMap[props.type][key],
                };
            });
        });

        const isMoreServicesExists = computed(() => {
            return services.value.moreServices.length;
        });

        const currentCityName = computed(() => {
            const isCityInRegion = props.currentRegion?.cities?.find((city) => {
                return city?.title?.includes(props.currentCity?.title);
            });

            if (isCityInRegion) {
                return getFormattedCityTitle(props.currentCity);
            }

            return 'Все города';
        });

        const hiddenActiveServicesCount = computed(() => {
            return services.value?.moreServices.filter((service) => {
                return props.activeServices.includes(service?.value);
            }).length ?? 0;
        });

        watch(() => props.type, () => {
            services.value.visibleServices = filters.value.slice(0, 3);
            services.value.moreServices = filters.value.slice(3);
            activeVisibleServices.value = props.activeServices;
        }, {immediate: true});

        watch(() => props.activeServices, (newValue) => {
            if (!newValue?.length) {
                activeVisibleServices.value = [];
            }
        }, {deep: true});

        function getFormattedCityTitle(city, isFormatted = true) {
            const {title: cityTitle, city_type: cityType = ''} = city;

            const removeEndsDot = (string) => string.endsWith('.') ? string.slice(0, -1) : string;

            return cityTitle === MAIN_CITY && !isFormatted
                ? `<b><span class="oc-location__city-type">${removeEndsDot(cityType)}</span> ${cityTitle}</b>`
                : `<span class="oc-location__city-type">${removeEndsDot(cityType)}</span> ${cityTitle}`;
        }

        function getFiltredAndSortedArray(array, filter) {
            return array
                .slice(0)
                .filter((item) => item[filter])
                .sort((a, b) => a.title > b.title ? 1 : -1);
        }

        function onRegionChooseHandler(region) {
            if (region.title === props.currentRegion.title) {
                return;
            }

            isOpenLocalList.value = false;
            context.emit('update:region', clone(region));
        }

        function onCityChooseHandler(city = null) {
            if (city && city?.title === props.currentCity?.title) {
                return;
            }

            isOpenAllCities.value = false;
            context.emit('update:city', city);
        }

        function setServices(services) {
            isOpenServices.value = false;
            context.emit('update:services', services);
        }

        function onChangeCheckboxHandler() {
            context.emit('update:services', activeVisibleServices.value);
        }

        function isCurrentCity(city) {
            return props.currentCity?.title === city?.title ?? false;
        }

        return {
            isOpenLocalList,
            isOpenAllCities,
            isOpenServices,
            services,
            activeVisibleServices,
            filtredRegions,
            filters,
            isMoreServicesExists,
            currentCityName,
            hiddenActiveServicesCount,
            splitToChunks,
            getFormattedCityTitle,
            getFiltredAndSortedArray,
            onRegionChooseHandler,
            onCityChooseHandler,
            setServices,
            onChangeCheckboxHandler,
            isCurrentCity,
        };
    },
});
</script>

<style lang="scss">
@import '@/assets/styles/components/about/offices/offices-controls';
</style>
